/* global newrelic */
import get from 'lodash.get';
import { graphql, readInlineData } from 'react-relay/legacy';
import { sessionStorage } from 'dibs-browser-storage';
import CheckoutMutationWrapper from '../mutations/CheckoutMutationWrapper';
import { getEmailKey } from '../helpers/checkoutStorage';
import { isLoggedIn, isUserLoginRequired } from '../helpers/checkoutUser';
import { isUserComesFromEmail, getParamFromUrl } from '../helpers/checkoutRedirects';
import {
    initializeDataLayer,
    storeEntryValues,
    trackPageViewOnce,
    trackEntryActionOnce,
    trackPaneStep,
    trackPurchaseSource,
    trackAddToCart,
    trackEmbeddedRegistration,
} from '../helpers/checkoutTracking';
import { STEPS } from '../constants/checkoutConstants';
import {
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
} from 'dibs-tracking';
import { trackPageViewAction } from '../reducers/checkoutPaneReducer';
import { getPrerenderedCheckoutState } from '../prerender/prerenderedCheckoutStore';

const checkoutTrackingActionsItemFragment = graphql`
    fragment checkoutTrackingActions_item on Item @inline {
        serviceId
        title
        isAvailable
        seller {
            serviceId
        }
        pricing {
            negotiable
        }
        classification {
            categories {
                category {
                    browseUrl
                }
            }
        }
        returnPolicy {
            accept
        }
        address {
            country
        }
    }
`;

const checkoutSetUpActionsBuyerFragment = graphql`
    fragment checkoutTrackingActions_buyer on User @inline {
        serviceId
    }
`;

const checkoutSetUpActionsTransactionFragment = graphql`
    fragment checkoutTrackingActions_transaction on Transaction @inline {
        serviceId
        isPrivateOffer
        isAutomatedOffer
        mostRecentOffer {
            quantity
            amountPerItem {
                convertedAmounts {
                    USD
                }
            }
        }
    }
`;

function getItemStatus(item) {
    return [
        `pur=${get(item, 'isAvailable') ? 'y' : 'n'}`,
        `neg=${get(item, 'pricing.negotiable') ? 'y' : 'n'}`,
        `ret=${get(item, 'returnPolicy.accept') ? 'y' : 'n'}`,
    ].join('|');
}

function getItemInfo(item, transaction) {
    return {
        id: get(item, 'serviceId'),
        name: get(item, 'title'),
        brand: get(item, 'seller.serviceId'),
        category: get(item, 'classification.categories[0].category.browseUrl').replace(/^\//, ''),
        price: get(transaction, 'mostRecentOffer.amountPerItem.convertedAmounts.USD'),
        quantity: get(transaction, 'mostRecentOffer.quantity'),
    };
}

export function initialTracking({
    itemRef,
    buyerRef,
    transactionRef,
    checkoutPageViewTracking = {},
    environment,
    initialPane,
    isMobile,
    country,
    isSelectedAddressValid,
}) {
    const item = readInlineData(checkoutTrackingActionsItemFragment, itemRef);
    const buyer = readInlineData(checkoutSetUpActionsBuyerFragment, buyerRef);
    const transaction = readInlineData(checkoutSetUpActionsTransactionFragment, transactionRef);
    const { serviceId: itemId, address: itemAddress } = item;
    const { serviceId: buyerId } = buyer || {};
    const { serviceId: orderId, isPrivateOffer, isAutomatedOffer } = transaction;
    const itemInfo = getItemInfo(item, transaction);
    const triggerType = getParamFromUrl('triggerType');
    const { rootState } = getPrerenderedCheckoutState();
    const sellerShippingCountry = itemAddress?.country;
    const buyerShippingCountry = !country
        ? 'no address'
        : `${country} - ${isSelectedAddressValid ? 'full' : 'partial'}`;
    const pageViewTracking = {
        ...checkoutPageViewTracking,
        ...(orderId && { orderId }),
        ...itemInfo,
        mobileAdaptive: isMobile,
        isPreRender: rootState === 'NOT_INITIALIZED' ? 'n' : 'y',
        pageType: 'Other',
        pageSubType: 'Checkout',
        pageLevel: 'Other',
        buyerShippingCountry,
        sellerShippingCountry,
        isPrivateOffer,
        isAutomatedOffer,
    };
    try {
        if (triggerType) {
            Object.assign(pageViewTracking, { triggerType });
        }

        // Reporting
        if (buyerId) {
            CheckoutMutationWrapper.commit('ReportCheckoutMutation', environment, {
                itemId,
                buyerId,
            });
        }

        // Tracking
        storeEntryValues({ initialPane, isPrivateOffer });
        initializeDataLayer(environment);
        trackPageViewOnce(pageViewTracking);
        trackEntryActionOnce({ initialPane });
        trackPaneStep({
            step: Object.values(STEPS).indexOf(initialPane) + 1,
            product: pageViewTracking,
        });

        if (isLoggedIn()) {
            const hasTrackedEmail = sessionStorage.getItem(getEmailKey(itemId));

            if (isUserComesFromEmail() && !hasTrackedEmail) {
                const tmpl = (getParamFromUrl('utm_campaign') || '')
                    .toLowerCase()
                    .replace(/\+/g, ' ');
                const label = getItemStatus(item);
                const action = `email ${isPrivateOffer ? 'accept offer' : 'purchase'} button - ${
                    tmpl || 'none'
                }`;
                sessionStorage.setItem(getEmailKey(itemId), true);
                trackPurchaseSource({
                    action,
                    label,
                    isInteractiveEvent: true,
                    eventName: eventNameConstants.EVENT_PRODUCT_INTERACTION,
                    interaction_type: interactionTypeConstants.INTERACTION_TYPE_ADD_TO_CART,
                    step_interaction_name: stepInteractionConstants.STEP_INTERACTION_ADD_TO_CART,
                });
                trackAddToCart(itemInfo);
            }
        } else if (!isUserLoginRequired()) {
            trackEmbeddedRegistration();
        }
        return trackPageViewAction({
            pageViewTracking,
        });
    } catch (err) {
        if (typeof newrelic !== 'undefined') {
            newrelic.noticeError(err);
        }
        return { type: 'CHECKOUT_LOAD_ERROR', err };
    }
}
